/**
 * Bulletin PDF generation utilities
 * - Single bulletin generation
 * - Batch generation (all employees)
 * - QR code generation for verification
 */

import { pdf } from '@react-pdf/renderer'
import { createElement } from 'react'
import QRCode from 'qrcode'
import { BulletinPDF } from './BulletinPDF'
import type { BulletinData } from './bulletin-types'

const VERIFY_BASE_URL = 'https://mirey.app/verify'

/**
 * Generate a QR code data URL for bulletin verification.
 */
export async function generateVerificationQR(
  token: string,
): Promise<string> {
  const verifyUrl = `${VERIFY_BASE_URL}/${token}`
  return QRCode.toDataURL(verifyUrl, {
    width: 200,
    margin: 1,
    color: {
      dark: '#111827',
      light: '#ffffff',
    },
    errorCorrectionLevel: 'M',
  })
}

/**
 * Generate a single bulletin PDF blob.
 */
export async function generateBulletinPDF(
  data: BulletinData,
): Promise<Blob> {
  const element = createElement(BulletinPDF, { data })
  // @ts-expect-error — react-pdf types mismatch with React 19
  const blob = await pdf(element).toBlob()
  return blob
}

/**
 * Generate and download a single bulletin PDF.
 */
export async function downloadBulletinPDF(
  data: BulletinData,
): Promise<void> {
  const blob = await generateBulletinPDF(data)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `bulletin_${data.employee.lastName}_${data.employee.firstName}_${data.period.year}_${String(data.period.month).padStart(2, '0')}.pdf`

  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Generate bulletins for multiple employees (batch).
 * Returns an array of { employeeId, blob, filename }.
 *
 * Each bulletin gets its own verification token + QR code.
 */
export async function generateBatchBulletins(
  bulletins: BulletinData[],
  onProgress?: (current: number, total: number) => void,
): Promise<Array<{
  employeeId: string
  fileName: string
  blob: Blob
}>> {
  const results: Array<{
    employeeId: string
    fileName: string
    blob: Blob
  }> = []

  for (let i = 0; i < bulletins.length; i++) {
    const data = bulletins[i]!
    onProgress?.(i + 1, bulletins.length)

    const blob = await generateBulletinPDF(data)
    const fileName = `bulletin_${data.employee.lastName}_${data.employee.firstName}_${data.period.year}_${String(data.period.month).padStart(2, '0')}.pdf`

    results.push({
      employeeId: data.employee.codeSal,
      fileName,
      blob,
    })
  }

  return results
}

/**
 * Download all bulletins as individual files (triggers multiple downloads).
 * For ZIP archive, use the vault API instead.
 */
export async function downloadBatchBulletins(
  bulletins: BulletinData[],
  onProgress?: (current: number, total: number) => void,
): Promise<void> {
  const results = await generateBatchBulletins(bulletins, onProgress)

  for (const result of results) {
    const url = URL.createObjectURL(result.blob)
    const a = document.createElement('a')
    a.href = url
    a.download = result.fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    // Small delay between downloads to avoid browser blocking
    await new Promise(resolve => setTimeout(resolve, 200))
  }
}
